"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Badge } from "@/components/ui/badge";
import { Clock, TrendingDown, Users, Zap, Shield, ChartBar } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function WarumDerVirtuelleAssistent() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const scrollProgress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
      setParallaxOffset(scrollProgress * 80);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const benefits = [{
    icon: Clock,
    title: "24/7 Erreichbarkeit",
    description: "Nie wieder verpasste Anrufe, auch außerhalb der Geschäftszeiten"
  }, {
    icon: TrendingDown,
    title: "Kostenersparnis",
    description: "Reduzieren Sie Ihre Personalkosten um bis zu 70%"
  }, {
    icon: Users,
    title: "Professioneller erster Eindruck",
    description: "Konsistente, freundliche Kommunikation bei jedem Anruf"
  }, {
    icon: Zap,
    title: "Sofortige Antworten",
    description: "Keine Warteschleifen, direkte Bearbeitung von Anfragen"
  }, {
    icon: Shield,
    title: "Datenschutz & Sicherheit",
    description: "DSGVO-konforme Verarbeitung aller Kundendaten"
  }, {
    icon: ChartBar,
    title: "Skalierbarkeit",
    description: "Wächst mit Ihrem Unternehmen ohne zusätzliche Kosten"
  }];
  return <section ref={sectionRef} className="relative py-16 md:py-24 lg:py-32 bg-background overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className={`relative transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`} style={{
          transform: `translateY(-${parallaxOffset}px)`
        }}>
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-medium">
              <img src="https://strong-warthog.10web.cloud/wp-content/uploads/2026/01/Wie_KIRezeptionisten_Ihre_Erreichbarkeit_247_verbessern.jpg" alt="KI-Rezeptionist Dashboard Interface" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent pointer-events-none" />
            </div>
            
            {}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent/20 rounded-full blur-3xl pointer-events-none" />
          </div>

          {}
          <div className={`transition-all duration-700 delay-100 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <h2 className="text-4xl md:text-4xl lg:text-5xl font-display font-bold text-foreground leading-tight mb-4">
              Warum der Virtuelle Assistent?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-10 md:mb-10">
              Unser KI-Rezeptionist revolutioniert die Art und Weise, wie Unternehmen mit ihren Kunden kommunizieren. Entdecken Sie die Vorteile einer intelligenten Lösung.
            </p>

            <div className="space-y-5">
              {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return <div key={i} data-index={i} className={`group transition-all duration-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: `${200 + i * 80}ms`
              }}>
                    <Badge variant="outline" className="w-full justify-start p-5 sm:p-4 hover:bg-primary/5 hover:border-primary/30 cursor-pointer transition-all duration-200">
                      <div className="flex items-start gap-3 sm:gap-3 w-full">
                        <div className="shrink-0 w-10 h-10 sm:w-10 sm:h-10 rounded-md bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-200">
                          <Icon className="w-5 h-5 sm:w-5 sm:h-5 text-primary" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-base sm:text-base text-foreground mb-1 leading-snug">
                            {benefit.title}
                          </div>
                          <div className="text-sm sm:text-sm text-muted-foreground font-normal leading-relaxed">
                            {benefit.description}
                          </div>
                        </div>
                      </div>
                    </Badge>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}